import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { numberUtils, useIsScreenDesktop } from '@zg-rentals/rental-manager-env';
import { Box, Combobox, FormField, Heading, Icon, Label, Table, Text, Tooltip } from '@zillow/constellation-10';
import { IconQuestionMarkCircleFilled } from '@zillow/constellation-icons';
import React from 'react';
import { emitSetFilterEvent, EVENT_IDS } from '../../model/ClickstreamEvents';
import type { InvoiceItemFilters } from '../../model/InvoiceItemFilters';
import billingUtils from '../../utils/billingUtils';
import { calculateFilters } from './calculateFilters';
import { StyledContainer } from './StyledComponents';
import {
  type AdproductsZuoraBillingInvoiceDto,
  type AdproductsZuoraBillingInvoiceItemDto,
  ZuoraInvoiceType,
} from '@zg-rentals/ts-adproducts-client';

export interface InvoiceItemDetailsTableProps {
  currentInvoice: AdproductsZuoraBillingInvoiceDto;
}

export const InvoiceItemDetailsTable = ({ currentInvoice }: InvoiceItemDetailsTableProps) => {
  const isScreenDesktop = useIsScreenDesktop();
  const { user } = useRentalManagerUser();
  const [currentInvoiceLineItems, setCurrentInvoiceLineItems] = React.useState<
    Array<AdproductsZuoraBillingInvoiceItemDto>
  >([]);
  const [invoiceItemFilters, setInvoiceItemFitlers] = React.useState<InvoiceItemFilters>({});
  const isFlatFeeFeedConnect = billingUtils.isFlatFeeFeedConnectInvoice(currentInvoiceLineItems);

  React.useEffect(() => {
    if (currentInvoice) {
      const newFilters = calculateFilters(currentInvoice);
      setInvoiceItemFitlers(newFilters);
    }
  }, [currentInvoice]);

  const chargeItemTooltipMessage = isFlatFeeFeedConnect
    ? 'Billed a flat rate per month at the account level regardless of number of listings posted.'
    : 'Billed per published unit per day.';

  const isMultifamily = currentInvoice.invoiceType === ZuoraInvoiceType.MULTIFAMILY;
  const alignments: Array<'start' | 'end'> = [];
  if (isMultifamily) alignments.push('start');
  alignments.push('start');
  if (isScreenDesktop) {
    alignments.push('end', 'end');
  }
  alignments.push('end');

  React.useEffect(() => {
    if (!currentInvoice) return setCurrentInvoiceLineItems([]);
    if (!Object.keys(invoiceItemFilters).length) return setCurrentInvoiceLineItems(currentInvoice.zuoraInvoiceItems);
    let filteredItems = currentInvoice.zuoraInvoiceItems.slice();
    if (invoiceItemFilters.chargeName) {
      const filterSet = new Set<string>();
      invoiceItemFilters.chargeName.options.forEach((v) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.chargeName));
      }
    }
    if (invoiceItemFilters.city) {
      const filterSet = new Set<string>();
      invoiceItemFilters.city.options.forEach((v) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.city));
      }
    }
    if (invoiceItemFilters.propertyName) {
      const filterSet = new Set<string>();
      invoiceItemFilters.propertyName.options.forEach((v) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.propertyName));
      }
    }
    if (invoiceItemFilters.state) {
      const filterSet = new Set<string>();
      invoiceItemFilters.state.options.forEach((v) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.state));
      }
    }

    setCurrentInvoiceLineItems(filteredItems);
  }, [invoiceItemFilters, currentInvoice]);

  if (currentInvoice.invoiceType !== ZuoraInvoiceType.MULTIFAMILY) {
    // wait until surfaceing sfh boost order items before surfacing
    if (
      !currentInvoice.zuoraInvoiceItems
        .map((item) => item.chargeName.toLowerCase())
        .some((chargeName) => chargeName.includes('boost') || chargeName.includes('spotlight'))
    ) {
      return null;
    }
  }

  const hasFilters =
    invoiceItemFilters.propertyName ||
    invoiceItemFilters.chargeName ||
    invoiceItemFilters.city ||
    invoiceItemFilters.state;

  const handleFilterChange = (key: keyof InvoiceItemFilters, values: Array<string>) => {
    emitSetFilterEvent({ user });
    setInvoiceItemFitlers((prev) => ({
      ...prev,
      [key]: prev[key]
        ? {
            ...prev[key],
            options: prev[key].options.map((opt) => ({
              ...opt,
              isChecked: values.includes(opt.label),
            })),
          }
        : undefined,
    }));
  };

  const renderCombobox = (key: keyof InvoiceItemFilters, label: string) => {
    const filter = invoiceItemFilters[key];
    if (!filter) return null;
    return (
      <Box
        css={{
          width: '100%',
          lg: { width: 'calc(50% - 20px)' },
          xxl: { width: 'calc(33.333% - 14px)' },
        }}
      >
        <FormField
          data-eventid={EVENT_IDS.SET_FILTER}
          label={<Label>{label}</Label>}
          control={
            <Combobox
              size="sm"
              limitChipsShown={-1}
              options={filter.options
                .map((opt) => ({ value: opt.label, label: opt.label }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              value={filter.options.filter((opt) => opt.isChecked).map((opt) => opt.label)}
              defaultValue={[]}
              showLabelForValue={true}
              renderChipsOverflow={() => {
                const selectedCount = filter.options.filter((opt) => opt.isChecked).length;
                return <>{selectedCount > 0 && `+${selectedCount}`}</>;
              }}
              onChange={(values: Array<string>) => handleFilterChange(key, values)}
            />
          }
        />
      </Box>
    );
  };

  return (
    <StyledContainer data-eventid={EVENT_IDS.SET_FILTER} data-eventdescription="Set filter event">
      <Heading level={4}>Invoice details</Heading>
      <Box css={{ margin: 'xs' }} />
      {hasFilters && (
        <Box
          css={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            marginBottom: 'md',
          }}
        >
          {renderCombobox('propertyName', 'Property')}
          {renderCombobox('chargeName', 'Charge Item')}
          {renderCombobox('city', 'City')}
          {renderCombobox('state', 'State')}
        </Box>
      )}
      <Table.Root appearance="horizontal" aria-label="Invoice details table" alignments={alignments}>
        <Table.Header>
          <Table.Row>
            {isMultifamily && <Table.HeaderCell>Property</Table.HeaderCell>}
            <Table.HeaderCell>Charge Item</Table.HeaderCell>
            {isScreenDesktop && <Table.HeaderCell>QTY</Table.HeaderCell>}
            {isScreenDesktop && <Table.HeaderCell>Unit Price</Table.HeaderCell>}
            <Table.HeaderCell>Amount</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {currentInvoiceLineItems.map((item) => (
            <Table.Row key={item.chargeId}>
              {isMultifamily && <Table.Cell>{item.propertyName}</Table.Cell>}
              <Table.Cell>
                {item.chargeName.includes('Zillow Feed Connect') ? (
                  <Tooltip
                    useHoverProps={{ delay: 100, restMs: 100 }}
                    content={chargeItemTooltipMessage}
                    shouldAwaitInteractionResponse
                    trigger={
                      <Text textStyle="body-xs" css={{ display: 'flex', alignItems: 'center' }}>
                        {item.chargeName}
                        <Icon size="sm" css={{ marginLeft: 3 }}>
                          <IconQuestionMarkCircleFilled />
                        </Icon>
                      </Text>
                    }
                  />
                ) : (
                  item.chargeName
                )}
              </Table.Cell>
              {isScreenDesktop && (
                <Table.Cell>
                  {isNaN(item.quantity) ? '-' : item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(2)}
                </Table.Cell>
              )}
              {isScreenDesktop && (
                <Table.Cell>
                  {!item.quantity ? (
                    '-'
                  ) : (
                    <span>
                      {numberUtils.asCurrencyWithDefault(item.chargeAmount / item.quantity, '-')}
                      {billingUtils.formatDiscountRate(item) && <div>{billingUtils.formatDiscountRate(item)}</div>}
                    </span>
                  )}
                </Table.Cell>
              )}
              <Table.Cell>
                <span>
                  {numberUtils.asCurrency(item.chargeAmount)}
                  {billingUtils.formatDiscountAmount(item) && <div>{billingUtils.formatDiscountAmount(item)}</div>}
                </span>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      <Box css={{ paddingBottom: 'lg' }} />
    </StyledContainer>
  );
};
